import { useState } from 'react';
import { Loader2, Grid3X3 } from 'lucide-react';
import { format, addDays, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useInventory } from '@/hooks/api/useAvailability';

// ─── Page ────────────────────────────────────────────────────────────────────

export function AvailabilityGridPage() {
  const propertyId = useSelectedPropertyId();
  const [fromDate, setFromDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [toDate, setToDate] = useState(() => format(addDays(new Date(), 13), 'yyyy-MM-dd'));

  const { data: inventoryData, isLoading } = useInventory({
    propertyId,
    from: fromDate,
    to: toDate,
  });

  return (
    <div>
      <PageHeader
        title="Availability Grid"
        description="Room inventory and availability by date"
        actions={
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="From date"
            />
            <span className="text-gray-400 text-sm">to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="To date"
            />
          </div>
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : !inventoryData || !inventoryData.roomTypes || inventoryData.roomTypes.length === 0 ? (
        <EmptyState
          icon={Grid3X3}
          title="No availability data"
          description="Select a property and date range to view inventory."
        />
      ) : (
        <AvailabilityGrid data={inventoryData} />
      )}
    </div>
  );
}

// ─── Grid Component ──────────────────────────────────────────────────────────

function AvailabilityGrid({
  data,
}: {
  data: {
    dates: string[];
    roomTypes: {
      roomTypeId: string;
      roomTypeName: string;
      cells: {
        date: string;
        totalRooms: number;
        available: number;
        sold: number;
        outOfOrder: number;
      }[];
    }[];
    summary: {
      cells: {
        date: string;
        totalRooms: number;
        available: number;
        sold: number;
        outOfOrder: number;
      }[];
    };
  };
}) {
  const dates = data.dates;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          {/* Date header */}
          <thead>
            <tr className="bg-gray-50">
              <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[180px] border-r border-gray-200">
                Room Type
              </th>
              {dates.map((d) => {
                let label: string;
                try {
                  label = format(parseISO(d), 'EEE MMM d');
                } catch {
                  label = d;
                }
                return (
                  <th
                    key={d}
                    className="px-3 py-3 text-center text-xs font-semibold text-gray-500 min-w-[100px] border-r border-gray-100"
                  >
                    {label}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {/* Room type rows */}
            {data.roomTypes.map((rt) => (
              <tr key={rt.roomTypeId} className="hover:bg-gray-50/50">
                <td className="sticky left-0 z-10 bg-white px-4 py-3 border-r border-gray-200">
                  <div className="font-medium text-sm text-gray-900 capitalize">{rt.roomTypeName}</div>
                </td>
                {rt.cells.map((cell) => (
                  <td key={cell.date} className="px-3 py-2 text-center border-r border-gray-100">
                    <InventoryCell cell={cell} />
                  </td>
                ))}
              </tr>
            ))}

            {/* Summary row */}
            {data.summary.cells.length > 0 && (
              <tr className="bg-gray-50 font-semibold">
                <td className="sticky left-0 z-10 bg-gray-50 px-4 py-3 border-r border-gray-200 text-sm text-gray-700">
                  Total
                </td>
                {data.summary.cells.map((cell) => (
                  <td key={cell.date} className="px-3 py-2 text-center border-r border-gray-100">
                    <InventoryCell cell={cell} />
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="border-t border-gray-200 px-4 py-3 flex items-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-green-100 border border-green-300" aria-hidden="true" />
          Available
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-blue-100 border border-blue-300" aria-hidden="true" />
          Sold
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-red-100 border border-red-300" aria-hidden="true" />
          Out of Order
        </span>
      </div>
    </div>
  );
}

// ─── Cell ────────────────────────────────────────────────────────────────────

function InventoryCell({
  cell,
}: {
  cell: {
    totalRooms: number;
    available: number;
    sold: number;
    outOfOrder: number;
  };
}) {
  const ratio = cell.totalRooms > 0 ? cell.available / cell.totalRooms : 0;
  const bgClass = ratio > 0.5
    ? 'bg-green-50 text-green-700'
    : ratio > 0.2
      ? 'bg-yellow-50 text-yellow-700'
      : 'bg-red-50 text-red-700';

  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className={cn('inline-flex items-center rounded px-2 py-0.5 text-xs font-bold', bgClass)}>
        {cell.available}
      </span>
      <div className="flex items-center gap-1 text-[10px] text-gray-400">
        {cell.sold > 0 && (
          <span className="text-blue-500" title="Sold">{cell.sold}s</span>
        )}
        {cell.outOfOrder > 0 && (
          <span className="text-red-500" title="Out of Order">{cell.outOfOrder}oo</span>
        )}
      </div>
    </div>
  );
}
